import React, { useState } from "react";

const ConsentFormDetails = ({ formData, setFormData, handleChange }) => {
    const [consentLang, setConsentLang] = useState("en");

    // ✅ Local masked handler ONLY for contact
    const handleContactChange = (e) => {
        const { value } = e.target;

        const digits = value.replace(/\D/g, "").slice(0, 11);

        let finalValue = "";
        if (digits.length <= 4) {
            finalValue = digits;
        } else {
            finalValue = `${digits.slice(0, 4)}-${digits.slice(4)}`;
        }

        setFormData({
            ...formData,
            contact: finalValue,
        });
    };

    /* ================= CONSENT CONTENT ================= */

    const consentEnglish = (
        <ol className="small">
            <li>I allow the patient to be admitted and treated according to their current psychiatric and medical condition.</li>
            <li>I confirm that all pre-existing physical or mental conditions have been truthfully disclosed.</li>
            <li>I allow the clinical team to assess, diagnose, and treat the patient as required.</li>
            <li>I allow prescribed medications and understand benefits and side effects.</li>
            <li>The treatment plan may change at the psychiatrist’s discretion.</li>
            <li>If the patient becomes harmful or aggressive, approved safety techniques may be used.</li>
            <li>In emergencies, the facility may transfer the patient if required.</li>
            <li>Treatment duration depends entirely on the patient’s condition.</li>
            <li>Doctor meetings depend on availability and schedule.</li>
            <li>Clinical information will only be shared within the treatment team.</li>
            <li>I will follow all facility rules.</li>
            <li>The facility is not responsible for issues due to undisclosed conditions.</li>
            <li>I take responsibility for unsafe patient behavior.</li>
            <li>I confirm everything has been explained and I give voluntary consent.</li>
            <li>I have read and agree to the family handbook.</li>
        </ol>
    );

    const consentUrdu = (
        <ol className="small" dir="rtl">
            <li>میں مریض کے علاج اور داخلے کی اجازت دیتا/دیتی ہوں۔</li>
            <li>میں تصدیق کرتا/کرتی ہوں کہ تمام جسمانی و ذہنی معلومات درست فراہم کی گئی ہیں۔</li>
            <li>میں کلینیکل ٹیم کو تشخیص اور علاج کی اجازت دیتا/دیتی ہوں۔</li>
            <li>مجھے ادویات کے فوائد اور ممکنہ نقصانات سمجھا دیے گئے ہیں۔</li>
            <li>علاج میں تبدیلی ڈاکٹر کی صوابدید پر ہو سکتی ہے۔</li>
            <li>اگر مریض نقصان دہ ہو تو حفاظتی طریقے استعمال کیے جا سکتے ہیں۔</li>
            <li>ہنگامی حالت میں مریض کو منتقل کیا جا سکتا ہے۔</li>
            <li>علاج کا دورانیہ مریض کی حالت پر منحصر ہوگا۔</li>
            <li>ڈاکٹر سے ملاقات دستیابی کے مطابق ہوگی۔</li>
            <li>مریض کی معلومات صرف علاج کے مقصد کے لیے استعمال ہوں گی۔</li>
            <li>میں ادارے کے تمام قواعد و ضوابط پر عمل کروں گا/گی۔</li>
            <li>غلط یا نامکمل معلومات کی ذمہ داری مجھ پر ہوگی۔</li>
            <li>مریض کے غیر محفوظ رویے کی ذمہ داری میری ہوگی۔</li>
            <li>مجھے تمام تفصیلات سمجھا دی گئی ہیں اور میں رضامندی دیتا/دیتی ہوں۔</li>
        </ol>
    );

    return (
        <div id="consent-form" className="content p-4">
            {/* ================== CLIENT INFO ================== */}
            <div className="row g-3">
                <div className="col-md-12"> <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block px-2 py-1"> Client Information </small> </div>
                {/* ✅ INFO BUTTONS */}
                <div className="d-flex gap-2 my-4">
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        data-bs-toggle="modal"
                        data-bs-target="#consentModal"
                        onClick={() => setConsentLang("en")}
                    >
                        Consent (English)
                    </button>

                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        data-bs-toggle="modal"
                        data-bs-target="#consentModal"
                        onClick={() => setConsentLang("ur")}
                    >
                        رضامندی (اردو)
                    </button>
                </div>

                <div className="col-md-3">
                    <div className="form-floating form-floating-outline mb-3">
                        <input
                            type="text"
                            className="form-control"
                            name="clientName"
                            value={formData.clientName || ""}
                            onChange={handleChange}
                            placeholder="Client Name"
                            readOnly={!!formData.patientId || !!formData.patient_id}
                        />
                        <label>Client Name</label>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="form-floating form-floating-outline mb-3">
                        <input
                            type="text"
                            className="form-control"
                            name="guardianName"
                            value={formData.guardianName || ""}
                            onChange={handleChange}
                            placeholder="Guardian Name"
                        />
                        <label>Guardian Name</label>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="form-floating form-floating-outline mb-3">
                        <select
                            className="form-select"
                            name="relationToClient"
                            value={formData.relationToClient || ""}
                            onChange={handleChange}
                        >
                            <option value="">Select Relation</option>
                            <option value="Parent">Parent</option>
                            <option value="Sibling">Sibling</option>
                            <option value="Spouse">Spouse</option>
                            <option value="Guardian">Guardian</option>
                            <option value="Other">Other</option>
                        </select>
                        <label>Relation to Client</label>
                    </div>
                </div>

                <div className="col-md-2">
                    <div className="form-floating form-floating-outline mb-3">
                        <input
                            type="text"
                            className="form-control"
                            name="mrNo"
                            value={formData.mrNo || ""}
                            onChange={handleChange}
                            readOnly={!!formData.patientId || !!formData.patient_id}
                        />
                        <label>MR No.</label>
                    </div>
                </div>
            </div>

            <div className="row g-3">
                <div className="col-md-3">
                    <div className="form-floating form-floating-outline mb-3">
                        <input
                            type="text"
                            className="form-control"
                            name="contact"
                            value={formData.contact || ""}
                            onChange={handleContactChange}
                            placeholder="Contact"
                            readOnly={!!formData.patientId || !!formData.patient_id}
                        />
                        <label>Contact</label>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="form-floating form-floating-outline mb-3">
                        <input
                            type="text"
                            className="form-control"
                            name="address"
                            value={formData.address || ""}
                            onChange={handleChange}
                            readOnly={!!formData.patientId || !!formData.patient_id}
                        />
                        <label>Address</label>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="form-floating form-floating-outline mb-3">
                        <input
                            type="date"
                            className="form-control"
                            name="date"
                            value={formData.date || ""}
                            onChange={handleChange}
                        />
                        <label>Date</label>
                    </div>
                </div>
            </div>

            {/* ================= FINANCE INFO ================= */}
            <div className="row g-3 mt-3">
                <div className="col-md-12"> <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block px-2 py-1"> Finance Information </small> </div>
                <div className="col-md-3">
                    <div className="form-floating form-floating-outline mb-3">
                        <input
                            type="text"
                            className="form-control"
                            name="amount"
                            value={formData.amount || ""}
                            onChange={handleChange}
                            placeholder="Amount"
                        />
                        <label>Amount</label>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="form-floating form-floating-outline mb-3">
                        <select
                            className="form-select"
                            name="paymentStatus"
                            value={formData.paymentStatus || ""}
                            onChange={handleChange}
                        >
                            <option value="">Select Status</option>
                            <option value="Paid">Paid</option>
                            <option value="unpaid">Unpaid</option>
                        </select>
                        <label>Payment Status</label>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="form-floating form-floating-outline mb-3">
                        <input
                            type="text"
                            className="form-control"
                            name="referenceNo"
                            value={formData.referenceNo || ""}
                            onChange={handleChange}
                            placeholder="Reference No"
                        />
                        <label>Reference No</label>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="form-floating form-floating-outline mb-3">
                        <input
                            type="date"
                            className="form-control"
                            name="paymentDate"
                            value={formData.paymentDate || ""}
                            onChange={handleChange}
                        />
                        <label>Payment Date</label>
                    </div>
                </div>
            </div>

            {/* ================= CONSENT MODAL ================= */}
            <div className="modal fade" id="consentModal" tabIndex="-1">
                <div className="modal-dialog modal-lg modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {consentLang === "en"
                                    ? "Treatment Consent"
                                    : "علاج کے لیے رضامندی"}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                            ></button>
                        </div>

                        <div className="modal-body">
                            {consentLang === "en" ? consentEnglish : consentUrdu}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                data-bs-dismiss="modal"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConsentFormDetails;
